import { isSodimacStandalone } from '@digital-retail/store-manager';

import { useMemo } from 'react';
import constants from '../../../config/constants';
import { getUniqueOperators, getVariant } from '../../../utils/variant';
import WithUrl from '../../../utils/WithUrl';

/**
 * Custom hook that encapsulates all business logic for ProductSpecificationConnect
 * @param {Object} props - Component props
 * @returns {Object} - Processed data and handlers
 */
export const useProductSpecificationConnect = ({ product, appCtx }) => {
  const { regionCode, deviceType, store } = appCtx;
  const { id: productId, name: productName, currentVariant, variants } = product;

  const processedData = useMemo(() => {
    const variant = getVariant(variants, currentVariant);
    const { deviceCost = [], deviceCmrPoints: cmrPoints, monthlyCost } = variant;

    const activeVariant = variants?.find((v) => v.id === currentVariant);

    const productColorCode = activeVariant?.attributes?.colorName ?? '';

    const operators = getUniqueOperators(productColorCode, variants);

    const minProductPrice = Array.isArray(deviceCost) && deviceCost.length ? [deviceCost[0]] : [];

    const isViewPlanActive = operators.length > 0 && minProductPrice.length > 0 && monthlyCost;

    const displayPrice = minProductPrice.length > 0 ? minProductPrice : [];
    const isSoCom = isSodimacStandalone(store);

    return {
      variant,
      activeVariant,
      deviceCost,
      cmrPoints,
      monthlyCost,
      productColorCode,
      operators,
      minProductPrice,
      isViewPlanActive,
      displayPrice,
      isSoCom,
    };
  }, [currentVariant, variants]);

  const deviceInfo = useMemo(
    () => ({
      shouldRenderSpecifications: deviceType === constants.DEVICE_DESKTOP,
      isMobile: deviceType === constants.DEVICE_MOBILE,
      isMobileOrTablet: [constants.DEVICE_MOBILE, constants.DEVICE_TABLET].includes(deviceType),
    }),
    [deviceType]
  );

  const planUrl = useMemo(() => {
    if (!processedData.isViewPlanActive) return null;

    return WithUrl('PLAN_URL', regionCode, {
      custom: {
        productId,
        productName: productName?.replace('/', '-') ?? '',
        variantId: processedData.activeVariant?.id,
      },
    });
  }, [regionCode, productId, productName, processedData.activeVariant?.id, processedData.isViewPlanActive]);

  const productAttributes = useMemo(
    () => ({
      merchantCategoryId: product.merchantCategoryId,
      isPrimeAvailable: product.attributes?.isPrimeAvailable,
      isPublished: product.isPublished,
    }),
    [product.merchantCategoryId, product.attributes?.isPrimeAvailable, product.isPublished]
  );

  const computedValues = useMemo(
    () => ({
      shouldRender: Boolean(product && appCtx),
      hasDisplayPrice: processedData.displayPrice.length > 0,
      hasActiveVariant: Boolean(processedData.activeVariant),
      isReady: Boolean(processedData.variant && deviceInfo),
    }),
    [product, appCtx, processedData.displayPrice.length, processedData.activeVariant, processedData.variant, deviceInfo]
  );

  return {
    // Processed data with spread
    ...processedData,

    // Device information
    ...deviceInfo,

    // URLs and attributes
    planUrl,
    productAttributes,

    // Product identifiers
    productId,
    productName,
    regionCode,

    // Computed values with spread
    ...computedValues,
  };
};
